import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Sun, Moon, BookMarked, LogOut, Search, Menu, X, ShieldCheck } from "lucide-react";
import Logo from "./Logo";
import { useApp } from "@/context/AppContext";
import { useSiteLayout } from "@/lib/siteLayout";
import { SmartLink } from "./Footer";

export default function Navbar() {
  const { user, theme, toggleTheme, logout } = useApp();
  const nav = useNavigate();
  const loc = useLocation();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const NAV = (useSiteLayout()?.header_links || []).map((l) => ({ to: l.url, label: l.label, newTab: l.new_tab }));

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`);
  };

  const active = (to) => (to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 glass border-b border-border">
      <nav className="max-w-[1400px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
        <Logo />
        <div className="hidden lg:flex items-center gap-1 ml-4">
          {NAV.map((n, i) => (
            <SmartLink key={i} to={n.to} newTab={n.newTab} data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${active(n.to) ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              {n.label}
            </SmartLink>
          ))}
        </div>

        <form onSubmit={submit} className="hidden md:flex flex-1 max-w-md ml-auto relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input data-testid="nav-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search stories, authors, tropes..."
            className="w-full bg-secondary/60 border border-border rounded-full pl-10 pr-4 py-2 text-sm outline-none focus:border-primary transition-colors" />
        </form>

        <div className="flex items-center gap-2 ml-auto md:ml-2">
          <button onClick={toggleTheme} data-testid="theme-toggle" aria-label="Toggle theme"
            className="p-2 rounded-full hover:bg-secondary transition-colors">
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </button>
          {user && (
            <Link to="/library" data-testid="nav-library" className="p-2 rounded-full hover:bg-secondary transition-colors hidden sm:block">
              <BookMarked className="h-5 w-5" />
            </Link>
          )}
          {user === null && (
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/login" data-testid="nav-login" className="px-4 py-2 text-sm font-medium rounded-full hover:bg-secondary transition-colors">Login</Link>
              <Link to="/register" data-testid="nav-signup" className="px-4 py-2 text-sm font-semibold rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity">Sign Up</Link>
            </div>
          )}
          {user && (
            <>
              {user.role === "admin" && (
                <Link to="/admin" data-testid="nav-admin" className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide border border-border rounded-full hover:border-primary transition-colors">
                  <ShieldCheck className="h-4 w-4" /> Admin
                </Link>
              )}
              <Link to="/profile" data-testid="nav-profile" className="h-9 w-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-bold text-sm overflow-hidden">
                {user.avatar ? <img src={user.avatar} alt="" className="h-full w-full object-cover" /> : (user.name?.[0] || "F&").toUpperCase()}
              </Link>
              <button onClick={() => { logout(); nav("/"); }} data-testid="nav-logout" className="p-2 rounded-full hover:bg-secondary transition-colors hidden sm:block">
                <LogOut className="h-5 w-5" />
              </button>
            </>
          )}
          <button className="lg:hidden p-2" onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-btn">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-border bg-card px-4 py-4 space-y-1">
          {NAV.map((n, i) => (
            <SmartLink key={i} to={n.to} newTab={n.newTab} onClick={() => setOpen(false)} className="block px-3 py-2.5 rounded-lg hover:bg-secondary">{n.label}</SmartLink>
          ))}
          <form onSubmit={submit} className="pt-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="w-full bg-secondary border border-border rounded-full px-4 py-2 text-sm" />
          </form>
          {user === null ? (
            <div className="flex gap-2 pt-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 border border-border rounded-full">Login</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 bg-primary text-primary-foreground rounded-full">Sign Up</Link>
            </div>
          ) : (
            <div className="flex gap-2 pt-2">
              <Link to="/profile" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 border border-border rounded-full">Profile</Link>
              <Link to="/library" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 border border-border rounded-full">Library</Link>
              {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="flex-1 text-center px-4 py-2 border border-border rounded-full">Admin</Link>}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
